import React, { useMemo } from "react";
import * as THREE from "three";
import { SymbolType } from "../../../../../types/floorplannerTypes";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { DoorStyle } from "./door3DStyle";
import {
  buildPaneledLayout,
  PaneledLeafLayout,
} from "./doorPaneledLayout";
import DoorSlotMaterial from "./DoorSlotMaterial";

type OpacityMaterialProps = {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
};

type DoorLeaf3DProps = {
  symbol: SymbolType;
  style: DoorStyle;
  width: number;
  thickness: number;
  height: number;
  /** Leaf-local center of the panel box. */
  center: THREE.Vector3 | { x: number; y: number; z: number };
  /** Fallback color when no Ready material is assigned to doorPanel. */
  panelColor: THREE.Color;
  /** Fallback for glazed stiles when no doorPanel assignment. */
  frameColor?: THREE.Color;
  glassColor: THREE.Color;
  fillOpacity: number;
  opacityMaterialProps: OpacityMaterialProps;
  selected: boolean;
  /**
   * When true, Y is fixed at 0 (sliding leaf groups already offset onto the wall face).
   * Hinged leaves keep the full center.y from geometry.
   */
  flattenY?: boolean;
};

type FramePartProps = {
  symbol: SymbolType;
  position: [number, number, number];
  size: [number, number, number];
  color: THREE.Color;
  opacityMaterialProps: OpacityMaterialProps;
  selected: boolean;
  roughness?: number;
};

/**
 * One stile / rail / fill box. Leaf axes: X = width, Y = thickness, Z = height.
 */
const FramePart: React.FC<FramePartProps> = ({
  symbol,
  position,
  size,
  color,
  opacityMaterialProps,
  selected,
  roughness = 0.64,
}) => (
  <mesh position={position} castShadow receiveShadow>
    <boxGeometry args={size} />
    <DoorSlotMaterial
      symbol={symbol}
      slot="doorPanel"
      fallbackColor={color}
      fallbackRoughness={roughness}
      fallbackMetalness={0.03}
      {...opacityMaterialProps}
    />
    <SelectedMeshOutline visible={selected} />
  </mesh>
);

type PaneledDoorFrameMeshProps = {
  symbol: SymbolType;
  width: number;
  thickness: number;
  height: number;
  paneled: PaneledLeafLayout;
  panelColor: THREE.Color;
  fillFallback: THREE.Color;
  opacityMaterialProps: OpacityMaterialProps;
  selected: boolean;
};

/**
 * Stile-and-rail paneled leaf: discrete boxes (no ExtrudeGeometry holes/bevels).
 * Matches real carpentry so hardware sits on solid stile/lock-rail faces.
 */
const PaneledDoorFrameMesh: React.FC<PaneledDoorFrameMeshProps> = ({
  symbol,
  width,
  thickness,
  height,
  paneled,
  panelColor,
  fillFallback,
  opacityMaterialProps,
  selected,
}) => {
  const { stile, rail, muntin, muntinZ, lockRail, lockRailZ, railW } = paneled;

  return (
    <>
      <FramePart
        symbol={symbol}
        position={[-width / 2 + stile / 2, 0, 0]}
        size={[stile, thickness, height]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      <FramePart
        symbol={symbol}
        position={[width / 2 - stile / 2, 0, 0]}
        size={[stile, thickness, height]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      <FramePart
        symbol={symbol}
        position={[0, 0, height / 2 - rail / 2]}
        size={[railW, thickness, rail]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      <FramePart
        symbol={symbol}
        position={[0, 0, -height / 2 + rail / 2]}
        size={[railW, thickness, rail]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      <FramePart
        symbol={symbol}
        position={[0, 0, muntinZ]}
        size={[railW, thickness, muntin]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      <FramePart
        symbol={symbol}
        position={[0, 0, lockRailZ]}
        size={[railW, thickness, lockRail]}
        color={panelColor}
        opacityMaterialProps={opacityMaterialProps}
        selected={selected}
      />
      {paneled.panels.map((panel) => (
        <FramePart
          key={panel.id}
          symbol={symbol}
          position={[0, 0, panel.z]}
          size={[panel.w, paneled.fillThickness, panel.h]}
          color={fillFallback}
          opacityMaterialProps={opacityMaterialProps}
          selected={selected}
          roughness={0.78}
        />
      ))}
    </>
  );
};

/**
 * Procedural door leaf: flush slab, stile-and-rail paneled, or glazed insert.
 */
const DoorLeaf3D: React.FC<DoorLeaf3DProps> = ({
  symbol,
  style,
  width,
  thickness,
  height,
  center,
  panelColor,
  frameColor,
  glassColor,
  fillOpacity,
  opacityMaterialProps,
  selected,
  flattenY = false,
}) => {
  const cx = center.x;
  const cy = flattenY ? 0 : center.y;
  const cz = center.z;
  const rimFallback = frameColor ?? panelColor;

  const paneled = useMemo(
    () =>
      style === "paneled" ? buildPaneledLayout(width, thickness, height) : null,
    [style, width, thickness, height],
  );

  const fillFallback = useMemo(
    () => panelColor.clone().multiplyScalar(0.88),
    [panelColor],
  );

  const glazed = useMemo(() => {
    if (style !== "glazed") return null;
    // Wider stiles so lever/knob/bar sit on wood instead of covering the pane.
    const stile = Math.max(Math.min(width, height) * 0.2, 0.07);
    const glassW = Math.max(width - stile * 2, 0.01);
    const glassH = Math.max(height - stile * 2, 0.01);
    const glassT = thickness * 0.35;
    return { stile, glassW, glassH, glassT };
  }, [style, width, height, thickness]);

  if (style === "glazed" && glazed) {
    const { stile, glassW, glassH, glassT } = glazed;
    return (
      <group key="glazed" position={[cx, cy, cz]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[glassW, glassT, glassH]} />
          <meshPhysicalMaterial
            color={glassColor}
            roughness={0.1}
            metalness={0.1}
            transmission={0.88}
            ior={1.5}
            transparent
            opacity={0.45 * fillOpacity}
            depthWrite={false}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[-width / 2 + stile / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[stile, thickness, height]} />
          <DoorSlotMaterial
            symbol={symbol}
            slot="doorPanel"
            fallbackColor={rimFallback}
            fallbackRoughness={0.55}
            fallbackMetalness={0.05}
            {...opacityMaterialProps}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[width / 2 - stile / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[stile, thickness, height]} />
          <DoorSlotMaterial
            symbol={symbol}
            slot="doorPanel"
            fallbackColor={rimFallback}
            fallbackRoughness={0.55}
            fallbackMetalness={0.05}
            {...opacityMaterialProps}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[0, 0, height / 2 - stile / 2]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[glassW, thickness, stile]} />
          <DoorSlotMaterial
            symbol={symbol}
            slot="doorPanel"
            fallbackColor={rimFallback}
            fallbackRoughness={0.55}
            fallbackMetalness={0.05}
            {...opacityMaterialProps}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[0, 0, -height / 2 + stile / 2]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[glassW, thickness, stile]} />
          <DoorSlotMaterial
            symbol={symbol}
            slot="doorPanel"
            fallbackColor={rimFallback}
            fallbackRoughness={0.55}
            fallbackMetalness={0.05}
            {...opacityMaterialProps}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
      </group>
    );
  }

  if (style === "paneled" && paneled) {
    return (
      <group key="paneled" position={[cx, cy, cz]}>
        <PaneledDoorFrameMesh
          symbol={symbol}
          width={width}
          thickness={thickness}
          height={height}
          paneled={paneled}
          panelColor={panelColor}
          fillFallback={fillFallback}
          opacityMaterialProps={opacityMaterialProps}
          selected={selected}
        />
      </group>
    );
  }

  // Flush — same group positioning as glazed/paneled so style switches stay stable.
  return (
    <group key="flush" position={[cx, cy, cz]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[width, thickness, height]} />
        <DoorSlotMaterial
          symbol={symbol}
          slot="doorPanel"
          fallbackColor={panelColor}
          fallbackRoughness={0.64}
          fallbackMetalness={0.03}
          {...opacityMaterialProps}
        />
        <SelectedMeshOutline visible={selected} />
      </mesh>
    </group>
  );
};

export default DoorLeaf3D;
